import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Badge } from "@/components/ui/PageHeader";
import {
  changeTeamRole,
  getTeam,
  inviteToTeam,
  listMyTeamInvites,
  removeTeamMember,
  respondToTeamInvite,
  withdrawTeamInvite,
  type TeamRole,
} from "@/api/organiser-desk";

/**
 * An organiser page's team. A co-host runs every event except the money and
 * the team; a door helper opens check-in only, on the day. A member is asked
 * and joins on yes; until then they can do nothing. Only the owner changes
 * the team; the owner and the co-hosts read it.
 */
export function TeamPanel({ pageId }: { pageId: string }) {
  const qc = useQueryClient();
  const [userId, setUserId] = useState("");
  const [role, setRole] = useState<TeamRole>("co_host");
  const [msg, setMsg] = useState<string | null>(null);
  const { data, error } = useQuery({ queryKey: ["organiser-team", pageId], queryFn: () => getTeam(pageId), retry: false });
  const team = data?.data;

  async function run(work: () => Promise<unknown>, done: string) {
    setMsg(null);
    try {
      await work();
      setMsg(done);
      await qc.invalidateQueries({ queryKey: ["organiser-team", pageId] });
    } catch (e) {
      setMsg(e instanceof Error ? e.message : "That did not work");
    }
  }

  return (
    <section className="bg-white border border-gray-200 rounded-2xl p-5 space-y-3">
      <h3 className="font-semibold text-gray-900">Team</h3>
      {error && <p className="text-sm text-red-700">{(error as Error).message}</p>}
      <ul className="text-sm space-y-1">
        {team?.members.map((m) => (
          <li key={m.userId} className="flex items-center gap-2">
            <span>{m.user.name}</span>
            <Badge variant={m.role === "owner" ? "green" : "blue"}>{m.role}</Badge>
            {m.role !== "owner" && (
              <>
                <button
                  className="text-xs underline"
                  onClick={() => void run(() => changeTeamRole(pageId, m.userId, m.role === "door" ? "co_host" : "door"), "Role changed")}
                >
                  make {m.role === "door" ? "co_host" : "door"}
                </button>
                <button className="text-xs text-red-700 underline" onClick={() => void run(() => removeTeamMember(pageId, m.userId), "Taken off")}>
                  take off
                </button>
              </>
            )}
          </li>
        ))}
        {team?.pendingInvites.map((i) => (
          <li key={i.id} className="flex items-center gap-2 text-gray-600">
            <span>{i.invitee.name}</span>
            <Badge variant="yellow">asked · {i.role}</Badge>
            <button className="text-xs underline" onClick={() => void run(() => withdrawTeamInvite(pageId, i.id), "Ask taken back")}>
              take back
            </button>
          </li>
        ))}
      </ul>
      <div className="flex gap-2 flex-wrap items-center">
        <input className="input w-80" placeholder="Member id to ask" value={userId} onChange={(e) => setUserId(e.target.value)} />
        <select className="input" value={role} onChange={(e) => setRole(e.target.value as TeamRole)}>
          <option value="co_host">co_host</option>
          <option value="door">door</option>
        </select>
        <Button size="sm" disabled={!userId.trim()} onClick={() => void run(() => inviteToTeam(pageId, userId.trim(), role), "Asked")}>
          Ask
        </Button>
      </div>
      {msg && <p className="text-xs text-gray-600">{msg}</p>}
    </section>
  );
}

/** The asks to join an organiser team that wait for the signed-in member's answer. */
export function MyTeamInvitesPanel() {
  const qc = useQueryClient();
  const [msg, setMsg] = useState<string | null>(null);
  const { data } = useQuery({ queryKey: ["my-team-invites"], queryFn: listMyTeamInvites });
  const invites = data?.data.invites ?? [];

  async function answer(inviteId: string, accept: boolean) {
    setMsg(null);
    try {
      const res = await respondToTeamInvite(inviteId, accept);
      setMsg(`Answered: ${res.data.status}`);
      await qc.invalidateQueries({ queryKey: ["my-team-invites"] });
    } catch (e) {
      setMsg(e instanceof Error ? e.message : "That did not work");
    }
  }

  return (
    <section className="bg-white border border-gray-200 rounded-2xl p-5 space-y-2">
      <h3 className="font-semibold text-gray-900">Asked to help run a page ({invites.length})</h3>
      <ul className="text-sm space-y-1">
        {invites.map((i) => (
          <li key={i.id} className="flex items-center gap-2">
            <span>
              {i.invitedBy.name} asked you to be <strong>{i.role}</strong> for {i.page.displayName}
            </span>
            <button className="text-xs underline" onClick={() => void answer(i.id, true)}>yes</button>
            <button className="text-xs underline" onClick={() => void answer(i.id, false)}>no</button>
          </li>
        ))}
      </ul>
      {msg && <p className="text-xs text-gray-600">{msg}</p>}
    </section>
  );
}
